import {
    Context,
    Span,
    SpanOptions,
    Exception,
    Link,
    SpanAttributes,
    SpanAttributeValue,
    SpanContext,
    SpanStatus,
    TimeInput,
} from '@opentelemetry/api';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';

// NOTE(bjarne): Simple interface using OpenTelemetry's tracing functions.
export interface ITracer {
    startSpan: (name: string, options?: SpanOptions, context?: Context) => Span;
    startActiveSpan: {
        <F extends (span: Span) => unknown>(name: string, fn: F): ReturnType<F>;
        <F extends (span: Span) => unknown>(name: string, options: SpanOptions, fn: F): ReturnType<F>;
        <F extends (span: Span) => unknown>(name: string, options: SpanOptions, context: Context, fn: F): ReturnType<F>;
    };
}

class DummySpan implements Span {
    public spanContext(): SpanContext {
        return { spanId: '', traceFlags: 0, traceId: '' };
    }
    public setAttribute(_key: string, _value: SpanAttributeValue): this {
        return this;
    }
    public setAttributes(_attributes: SpanAttributes): this {
        return this;
    }
    public addEvent(_name: string, _attributesOrStartTime?: SpanAttributes | TimeInput, _startTime?: TimeInput): this {
        return this;
    }
    public addLink(_link: Link): this {
        return this;
    }
    public addLinks(_links: Link[]): this {
        return this;
    }
    public setStatus(_status: SpanStatus): this {
        return this;
    }
    public updateName(_name: string): this {
        return this;
    }
    public end(_endTime?: TimeInput): void {}
    public isRecording(): boolean {
        return false;
    }
    public recordException(_exception: Exception, _time?: TimeInput): void {}
}

export const TRACER_NAME = 'nmui4d-tracer';
export class ConnectionCheckingTracer implements ITracer {
    private delegatee: ITracer;
    private isTargetURLInvalid = false;

    public constructor(delegatee: ITracer, targetURL: URL) {
        this.delegatee = delegatee;
        void this.checkTargetAvailabilityAsync(targetURL);
    }

    private async checkTargetAvailabilityAsync(targetURL: URL) {
        try {
            await fetch(targetURL, { method: 'POST' });
            // NOTE(bjarne): To avoid this instrumentation spamming us with errors, we only register after
            // making sure we can reach the target URL.
            registerInstrumentations({ instrumentations: [new FetchInstrumentation()] });
        } catch (error) {
            this.isTargetURLInvalid = true;
            console.error(
                `Failed to connect to tracer backend '${targetURL}', disabling trace transmission for this session.`,
                error,
            );
        }
    }

    public startSpan(name: string, options?: SpanOptions, context?: Context): Span {
        if (this.isTargetURLInvalid) {
            return new DummySpan();
        }

        return this.delegatee.startSpan(name, options, context);
    }

    public startActiveSpan<F extends (span: Span) => unknown>(name: string, fn: F) {
        if (this.isTargetURLInvalid) {
            return this.delegatee.startActiveSpan(name, fn);
        }

        return fn(new DummySpan());
    }
}
